import React, { useState } from "react";
import { AxiosResponse } from "axios";
import { Input, Modal, Form, Rating, TextArea } from "semantic-ui-react";
import { postRequest } from "../../utils/httpRequest";
import { notification } from "../../utils/helpers";

interface Props {
  enrolmentId: number;
  courseName: string;
  showModal: boolean;
  handleHideNewReviewModal: (e: any) => void;
  refreshCertificate: () => void;
}

interface ReviewErrors {
  content?: string;
  rating?: string;
  server?: string;
}

const NewReviewModal: React.FC<Props> = ({
  enrolmentId,
  courseName,
  showModal,
  handleHideNewReviewModal,
  refreshCertificate,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(0);
  const [content, setContent] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [courseRequest, setCourseRequest] = useState("");
  const [errors, setErrors] = useState<ReviewErrors>({});

  const handleRating = (e, { rating }) => {
    setRating(rating);
    setErrors((currentErrors) => ({
      ...currentErrors,
      rating: undefined,
      server: undefined,
    }));
  };

  const validateReview = () => {
    const nextErrors = {} as ReviewErrors;

    if (!rating) {
      nextErrors.rating = "You must choose a rating.";
    }

    if (!content.trim()) {
      nextErrors.content = "Review content cannot be empty.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const submitReview = (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!validateReview()) return;

    setSubmitting(true);

    let review = {
      rating: rating,
      content: content,
      recommendations: recommendations,
      utm_source: utmSource,
      course_request: courseRequest,
    };

    postRequest(`/api/enrolments/${enrolmentId}/reviews`, review)
      .then((response: AxiosResponse) => {
        setSubmitting(false);
        notification("Thank you for your review!", "success");
        refreshCertificate();
        handleHideNewReviewModal(e);
      })
      .catch((error) => {
        setSubmitting(false);
        setErrors({
          server: error.response?.data?.errors || "Unable to submit review.",
        });
      });
  };

  return (
    <Modal
      closeIcon
      open={showModal}
      onClose={(e) => handleHideNewReviewModal(e)}
      size="tiny"
      className="new-review-modal"
    >
      <Modal.Content>
        <Form onSubmit={(e) => submitReview(e)}>
          <div className="review-header">
            <h2>Review this course</h2>
            <h1>{courseName}</h1>
            <p>
              Share what helped, what could be better, and what you would like
              to learn next.
            </p>
          </div>
          <Form.Field>
            <label>Overall, how would you rate the course?*</label>
            <Rating
              maxRating={5}
              clearable
              value={rating}
              defaultRating={rating}
              onRate={handleRating}
              icon="star"
              size="massive"
            />
            {errors.rating && (
              <div className="review-error">{errors.rating}</div>
            )}
          </Form.Field>

          <Form.Field>
            <label>Please share a short review to help others*</label>
            <TextArea
              rows={3}
              value={content}
              onChange={(e) => {
                setContent(e.target.value);
                setErrors((currentErrors) => ({
                  ...currentErrors,
                  content: undefined,
                  server: undefined,
                }));
              }}
            />
            {errors.content && (
              <div className="review-error">{errors.content}</div>
            )}
          </Form.Field>

          <Form.Field>
            <label>How can we make your experience even better?</label>
            <TextArea
              rows={3}
              value={recommendations}
              onChange={(e) => setRecommendations(e.target.value)}
            />
          </Form.Field>

          <Form.Field>
            <label>How did you hear about us?</label>
            <Input
              type="text"
              value={utmSource}
              onChange={(e) => setUtmSource(e.target.value)}
            />
          </Form.Field>

          <Form.Field>
            <label>What else would you like to learn from us?</label>
            <Input
              type="text"
              value={courseRequest}
              onChange={(e) => setCourseRequest(e.target.value)}
            />
          </Form.Field>

          {errors.server && <div className="review-error">{errors.server}</div>}

          <div className="review-actions">
            <button
              className="review-button review-button--ghost"
              onClick={(e) => handleHideNewReviewModal(e)}
              type="button"
            >
              Cancel
            </button>
            <button
              className={`review-button review-button--primary ${
                submitting ? "loading" : ""
              }`}
              disabled={submitting}
              type="submit"
            >
              Submit review
            </button>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

export default NewReviewModal;
